import React from 'react';
import { motion } from 'framer-motion';
import { format, isSameDay } from 'date-fns';

export default function CalendarDayView({ currentDate, appointments, onAppointmentClick, onDateClick }) {
  const hours = Array.from({ length: 24 }, (_, i) => i);

  const getAppointmentsForHour = (hour) => {
    return appointments?.filter(apt => {
      const aptDate = new Date(apt.startTime);
      return isSameDay(aptDate, currentDate) && aptDate?.getHours() === hour;
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: 'bg-blue-500',
      confirmed: 'bg-green-500',
      in_progress: 'bg-yellow-500',
      completed: 'bg-gray-500',
      cancelled: 'bg-red-500',
      no_show: 'bg-orange-500'
    };
    return colors?.[status] || 'bg-gray-500';
  };

  const handleTimeSlotClick = (hour) => {
    const dateTime = new Date(currentDate);
    dateTime?.setHours(hour, 0, 0, 0);
    onDateClick(dateTime);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.2 }}
      className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
    >
      {/* Day Header */}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        <h2 className="text-xl font-bold text-gray-900">
          {format(currentDate, 'EEEE, MMMM d, yyyy')}
        </h2>
      </div>
      {/* Time Slots */}
      <div className="overflow-auto max-h-[calc(100vh-300px)]">
        {hours?.map((hour) => {
          const hourAppointments = getAppointmentsForHour(hour);
          return (
            <div
              key={hour}
              onClick={() => handleTimeSlotClick(hour)}
              className="flex border-b border-gray-200 min-h-[80px] hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <div className="w-24 flex-shrink-0 px-4 py-3 text-sm text-gray-600 bg-gray-50 border-r border-gray-200">
                {format(new Date()?.setHours(hour, 0, 0, 0), 'h:mm a')}
              </div>
              <div className="flex-1 p-3 space-y-2">
                {hourAppointments?.map((apt) => (
                  <div
                    key={apt?.id}
                    onClick={(e) => {
                      e?.stopPropagation();
                      onAppointmentClick(apt);
                    }}
                    className={`${getStatusColor(
                      apt?.status
                    )} text-white p-4 rounded-lg cursor-pointer hover:opacity-90 transition-opacity`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="font-semibold text-lg">{apt?.title}</div>
                        <div className="text-sm opacity-90">
                          {format(new Date(apt.startTime), 'h:mm a')} -{' '}
                          {format(new Date(apt.endTime), 'h:mm a')}
                        </div>
                      </div>
                      <div className="text-xs bg-white bg-opacity-20 px-2 py-1 rounded">
                        {apt?.status?.replace('_', ' ')?.toUpperCase()}
                      </div>
                    </div>
                    {apt?.customer && (
                      <div className="text-sm opacity-90 mb-1">
                        <span className="font-medium">Customer:</span>{' '}
                        {apt?.customer?.company_name || apt?.customer?.contact_person}
                      </div>
                    )}
                    {apt?.service && (
                      <div className="text-sm opacity-90 mb-1">
                        <span className="font-medium">Service:</span> {apt?.service?.name}
                      </div>
                    )}
                    {apt?.assignedStaff && (
                      <div className="text-sm opacity-90">
                        <span className="font-medium">Staff:</span>{' '}
                        {apt?.assignedStaff?.first_name} {apt?.assignedStaff?.last_name}
                      </div>
                    )}
                    {apt?.description && (
                      <div className="text-sm opacity-90 mt-2 pt-2 border-t border-white border-opacity-20">
                        {apt?.description}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}